import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Tag,
  Copy,
  CheckCircle,
  Lightbulb,
  MessageSquare,
  Sparkles,
  Share2,
  AlertTriangle,
  FolderTree,
  HelpCircle
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import { getPromptBySlug, promptsData } from '../data/promptsData';
import { isGlossarySlug } from '../data/glossarySlugs';

const PromptDetailPage = () => {
  const { promptSlug } = useParams();
  const [copiedPrompt, setCopiedPrompt] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const prompt = getPromptBySlug(promptSlug);

  if (!prompt) {
    return <Navigate to="/prompts" replace />;
  }

  const currentIndex = promptsData.findIndex(p => p.slug === promptSlug);
  const prevPrompt = currentIndex > 0 ? promptsData[currentIndex - 1] : null;
  const nextPrompt = currentIndex < promptsData.length - 1 ? promptsData[currentIndex + 1] : null;

  const breadcrumbItems = [
    { label: 'Prompts', href: '/prompts' },
    { label: prompt.category, href: null },
    { label: prompt.title, href: null }
  ];

  // HowTo + FAQPage schema
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": prompt.title,
    "description": prompt.definition,
    "url": `https://www.getsortio.com/prompts/${prompt.slug}`,
    "dateModified": prompt.lastUpdated,
    "step": prompt.prompts.map((p, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": p.name,
      "text": p.prompt,
      "itemListElement": {
        "@type": "HowToDirection",
        "text": p.expectedResult
      }
    })),
    "publisher": {
      "@type": "Organization",
      "name": "Sortio",
      "url": "https://www.getsortio.com"
    }
  };

  const faqSchema = prompt.faq && prompt.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": prompt.faq.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  const schemas = [howToSchema];
  if (faqSchema) schemas.push(faqSchema);

  const handleCopyPrompt = async (promptText, index) => {
    try {
      await navigator.clipboard.writeText(promptText);
      setCopiedPrompt(index);
      setTimeout(() => setCopiedPrompt(null), 2000);
    } catch (err) {
      console.error('Failed to copy prompt:', err);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${prompt.title} - Sortio Prompts`,
          text: prompt.shortDescription,
          url: window.location.href,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      handleCopyLink();
    }
  };

  // Only link related terms that actually have a glossary page. These
  // lists are hand-maintained, so an unrecognised slug here would render
  // a link straight to a 404.
  const promptTerms = (prompt.relatedTerms || []).filter(isGlossarySlug);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#121212" }}>
      <SEOHead
        title={`${prompt.title} - AI Prompts | Sortio`}
        description={`${prompt.definition} Copy and paste these ready-to-use prompts into Sortio for instant file organization.`}
        canonical={`https://www.getsortio.com/prompts/${prompt.slug}`}
        schema={schemas}
        keywords={`${prompt.title}, file organization prompts, AI prompts, ${prompt.category.toLowerCase()} file organization, Sortio prompts`}
        lastModified={prompt.lastUpdated}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Header Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/prompts"
              className="flex items-center text-apple-400 hover:text-sortio-primary transition-colors"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back to Prompts
            </Link>

            <div className="flex items-center gap-2">
              <button
                onClick={handleShare}
                className="flex items-center px-3 py-2 bg-apple-700/40 text-apple-300 hover:text-sortio-primary rounded-lg transition-colors"
                title="Share this prompt pack"
              >
                <Share2 size={16} className="mr-1" />
                Share
              </button>
              <button
                onClick={handleCopyLink}
                className="flex items-center px-3 py-2 bg-apple-700/40 text-apple-300 hover:text-sortio-primary rounded-lg transition-colors"
                title="Copy link"
              >
                {copiedLink ? (
                  <>
                    <CheckCircle size={16} className="mr-1 text-green-400" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-1" />
                    Copy Link
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="mb-4">
            <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-sm font-medium rounded-lg">
              {prompt.category}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-semibold mb-6 text-apple-50">
            {prompt.title}
          </h1>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 mb-8">
            <p className="text-lg text-apple-100 leading-relaxed">
              {prompt.definition}
            </p>
          </div>

          <div className="flex items-center gap-6 text-sm text-apple-400">
            <div className="flex items-center">
              <Clock className="mr-2" size={16} />
              Last updated: {new Date(prompt.lastUpdated).toLocaleDateString()}
            </div>
            <div className="flex items-center">
              <Tag className="mr-2" size={16} />
              {prompt.prompts.length} prompts included
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="mb-12 bg-apple-700/20 rounded-2xl p-6">
          <h3 className="text-lg font-medium text-apple-50 mb-4">On This Page</h3>
          <nav className="space-y-2">
            <a href="#prompts" className="block text-apple-300 hover:text-sortio-primary transition-colors">
              Prompts
            </a>
            <a href="#customization" className="block text-apple-300 hover:text-sortio-primary transition-colors">
              Customization Tips
            </a>
            <a href="#troubleshooting" className="block text-apple-300 hover:text-sortio-primary transition-colors">
              Troubleshooting
            </a>
            {prompt.faq && prompt.faq.length > 0 && (
              <a href="#faq" className="block text-apple-300 hover:text-sortio-primary transition-colors">
                FAQ
              </a>
            )}
          </nav>
        </div>

        {/* Prompts Section */}
        <section id="prompts" className="mb-12">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
            <MessageSquare className="mr-3 text-sortio-primary" size={24} />
            Prompts
          </h2>
          <div className="space-y-6">
            {prompt.prompts.map((p, index) => (
              <div key={index} className="bg-apple-700/30 rounded-2xl overflow-hidden border border-apple-600/30">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-apple-50">{p.name}</h3>
                    <button
                      onClick={() => handleCopyPrompt(p.prompt, index)}
                      className="flex items-center px-3 py-1.5 bg-sortio-primary/10 text-sortio-primary hover:bg-sortio-primary/20 rounded-lg transition-colors text-sm"
                    >
                      {copiedPrompt === index ? (
                        <>
                          <CheckCircle size={14} className="mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy size={14} className="mr-1" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-apple-800/50 rounded-xl p-4 font-mono text-sm text-apple-200 mb-4">
                    {p.prompt}
                  </div>
                  <div className="flex items-start mb-4">
                    <CheckCircle className="text-green-400 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <div>
                      <span className="text-apple-400 text-sm">Expected result: </span>
                      <span className="text-apple-300 text-sm">{p.expectedResult}</span>
                    </div>
                  </div>
                  {p.folderTreeAfter && (
                    <div className="mt-4">
                      <div className="flex items-center text-apple-400 text-sm mb-2">
                        <FolderTree size={14} className="mr-1" />
                        Folder structure after:
                      </div>
                      <pre className="bg-apple-800/30 rounded-lg p-3 text-xs text-apple-300 overflow-x-auto">
                        {p.folderTreeAfter}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Customization Tips Section */}
        {prompt.customizationTips && prompt.customizationTips.length > 0 && (
          <section id="customization" className="mb-12">
            <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
              <Lightbulb className="mr-3 text-amber-400" size={24} />
              Customization Tips
            </h2>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6">
              <ul className="space-y-3">
                {prompt.customizationTips.map((tip, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="text-amber-400 mr-3 mt-0.5 flex-shrink-0" size={16} />
                    <span className="text-apple-300">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Troubleshooting Section */}
        {prompt.troubleshooting && prompt.troubleshooting.length > 0 && (
          <section id="troubleshooting" className="mb-12">
            <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
              <AlertTriangle className="mr-3 text-orange-400" size={24} />
              Troubleshooting
            </h2>
            <div className="space-y-6">
              {prompt.troubleshooting.map((item, index) => (
                <div key={index} className="bg-apple-700/30 rounded-2xl p-6 border border-apple-600/30">
                  <div className="flex items-start mb-4">
                    <AlertTriangle className="text-orange-400 mr-3 mt-1 flex-shrink-0" size={18} />
                    <div>
                      <h4 className="font-medium text-apple-50 mb-1">Problem:</h4>
                      <p className="text-apple-300">{item.problem}</p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={18} />
                    <div>
                      <h4 className="font-medium text-apple-50 mb-1">Solution:</h4>
                      <p className="text-apple-300">{item.solution}</p>
                    </div>
                  </div>
                  {item.steps && item.steps.length > 0 && (
                    <div className="ml-8 mt-4">
                      <h4 className="text-sm font-medium text-apple-400 mb-2">Steps:</h4>
                      <ol className="space-y-2">
                        {item.steps.map((step, stepIndex) => (
                          <li key={stepIndex} className="flex items-start text-sm text-apple-300">
                            <span className="w-5 h-5 bg-apple-600/40 rounded-full flex items-center justify-center text-xs mr-2 flex-shrink-0">
                              {stepIndex + 1}
                            </span>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FAQ Section */}
        {prompt.faq && prompt.faq.length > 0 && (
          <section id="faq" className="mb-12">
            <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
              <HelpCircle className="mr-3 text-sortio-primary" size={24} />
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {prompt.faq.map((faq, index) => (
                <div key={index} className="bg-apple-700/20 rounded-xl p-6">
                  <h3 className="font-medium text-apple-50 mb-3">{faq.question}</h3>
                  <p className="text-apple-300 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Related Terms */}
        {promptTerms.length > 0 && (
          <section className="mb-12">
            <h3 className="text-xl font-semibold text-apple-50 mb-6">Related Glossary Terms</h3>
            <div className="flex flex-wrap gap-2">
              {promptTerms.map((termSlug, index) => (
                <Link
                  key={index}
                  to={`/glossary/${termSlug}`}
                  className="px-4 py-2 bg-apple-700/30 text-apple-300 hover:text-sortio-primary hover:bg-apple-600/30 rounded-lg transition-colors text-sm"
                >
                  {termSlug.replace(/-/g, ' ')}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Ready to Try These Prompts?</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              Download Sortio and paste these prompts to instantly organize your files with AI-powered intelligence.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio Free
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>

        {/* Navigation */}
        <div className="flex justify-between items-center border-t border-apple-600/30 pt-8">
          {prevPrompt ? (
            <Link
              to={`/prompts/${prevPrompt.slug}`}
              className="flex items-center text-apple-400 hover:text-sortio-primary transition-colors group"
            >
              <ArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" size={16} />
              <div>
                <div className="text-xs uppercase tracking-wider mb-1">Previous</div>
                <div className="font-medium">{prevPrompt.title}</div>
              </div>
            </Link>
          ) : (
            <div></div>
          )}

          {nextPrompt ? (
            <Link
              to={`/prompts/${nextPrompt.slug}`}
              className="flex items-center text-apple-400 hover:text-sortio-primary transition-colors group text-right"
            >
              <div>
                <div className="text-xs uppercase tracking-wider mb-1">Next</div>
                <div className="font-medium">{nextPrompt.title}</div>
              </div>
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
            </Link>
          ) : (
            <div></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptDetailPage;
